import { Pressable, View } from "react-native";
import React, { useState } from "react";
import { SubscriptionDetails } from "features/types";
import WhiteText from "components/WhiteText";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { MainFlowParamList } from "navigation/types";
import Spacer from "components/Spacer";
import Chevron from "components/Chevron";
import Button from "components/Button";
import { toScehdule } from "utils/navigationUtils";
import { useAppDispatch } from "hooks/useAppDispatch";
import { setSelectedSubscription } from "features/slices/reservationFlowSlice";
import { getUniqSubscriptions } from "utils/subscriptionUtils";

export const VoucherSubDetails = ({
  currentSub,
  accountVoucherSubs,
  isParent,
  showCta = false,
}: {
  currentSub: SubscriptionDetails;
  accountVoucherSubs: SubscriptionDetails[];
  isParent?: boolean;
  showCta?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const subsWithTheSamePlan = accountVoucherSubs.filter(
    (sub) => sub.planPackage.id === currentSub.planPackage.id
  );
  const hideSeatCreditsCount = currentSub.planPackage.hideSeatCreditsCount;
  const childSubs = isParent
    ? accountVoucherSubs.filter(
        (sub) => sub.planPackage.parentId === currentSub.planPackage.id
      )
    : [];

  const getAvailableVouchers = (subs: SubscriptionDetails[]) => {
    return subs.reduce((acc, sub) => sub.availableVouchers + acc, 0);
  };

  const getPerGameMaxForSub = () => {
    if (childSubs.length === 0) {
      // If subscription has no children, return the perEventMax of the subscription
      return currentSub.perEventMax * subsWithTheSamePlan.length;
    } else {
      // If subscription has children, return the perEventMax of the first child subscription
      return childSubs[0].perEventMax * subsWithTheSamePlan.length;
    }
  };
  return (
    <View>
      <WhiteText category={isParent ? "h6" : "p2"}>
        {isParent ? currentSub.planPackage.name : currentSub.planPackage.family}
        {!isParent && !hideSeatCreditsCount ? (
          <WhiteText>: {getAvailableVouchers(subsWithTheSamePlan)}</WhiteText>
        ) : (
          ""
        )}
      </WhiteText>
      {isParent && !hideSeatCreditsCount ? (
        <View>
          <WhiteText category="p2">
            <WhiteText style={{ fontSize: 36, fontWeight: "700" }}>
              {getAvailableVouchers(subsWithTheSamePlan) +
                getAvailableVouchers(childSubs)}
            </WhiteText>{" "}
            Unused Seat Credits
          </WhiteText>
          {getPerGameMaxForSub() > 0 && (
            <WhiteText category="p2">
              {getPerGameMaxForSub()} Seat Membership
            </WhiteText>
          )}
        </View>
      ) : (
        <></>
      )}
      {currentSub.planPackage.ctaButtonText && showCta && (
        <Button
          appearance="outline"
          style={{ alignSelf: "flex-end" }}
          onPress={() => {
            dispatch(setSelectedSubscription(currentSub));
            toScehdule();
          }}
          size="small"
        >
          {currentSub.planPackage.ctaButtonText}
        </Button>
      )}
    </View>
  );
};

const VoucherDetails = (props: {
  accountVoucherSubs: SubscriptionDetails[];
}) => {
  const dispatch = useAppDispatch();
  const { accountVoucherSubs } = props;
  const navigation = useNavigation<NavigationProp<MainFlowParamList>>();
  const [showDetailsToggle, setShowDetailsToggle] = useState<boolean[]>([]);
  const parentSubs = accountVoucherSubs.filter((sub) => !sub.child);

  const getChildSubs = (parentSub: SubscriptionDetails) => {
    const parentId = parentSub.planPackage.id;
    return accountVoucherSubs.filter(
      (sub) => sub.planPackage.parentId === parentId
    );
  };

  return (
    <>
      {getUniqSubscriptions(parentSubs).map((sub, index) => {
        const childSubs = getUniqSubscriptions(getChildSubs(sub));
        const showAddSeatCredits = !["ADMIN", "NONE", "URL_ONLY"].includes(
          sub.planPackage.visibility
        );

        return (
          <View
            key={sub.id}
            className="border border-white rounded-xl p-4 mb-4"
          >
            <VoucherSubDetails
              currentSub={sub}
              accountVoucherSubs={accountVoucherSubs}
              isParent
            />

            <Spacer
              margin={5}
              className="flex-row justify-between items-center"
            >
              {childSubs.length > 0 && (
                <View className="items-start max-w-full">
                  <Pressable
                    className="flex-row items-center"
                    onPress={() => {
                      setShowDetailsToggle((toggle) => {
                        const newToggle = [...toggle];
                        newToggle[index] = !newToggle[index];
                        return newToggle;
                      });
                    }}
                  >
                    <WhiteText style={{ textDecorationLine: "underline" }}>
                      {showDetailsToggle[index] === true ? "Hide" : "Show"}{" "}
                      Details
                    </WhiteText>
                    <Chevron
                      color="white"
                      direction={showDetailsToggle[index] ? "up" : "down"}
                    />
                  </Pressable>
                </View>
              )}
              {showAddSeatCredits && (
                <Pressable
                  onPress={() => {
                    navigation.navigate("Account", {
                      screen: "ManageSubscriptions",
                      params: {
                        screen: "ManageSubscriptionsScreen",
                      },
                    });
                  }}
                >
                  <WhiteText style={{ textDecorationLine: "underline" }}>
                    Add Seat Credits
                  </WhiteText>
                </Pressable>
              )}
              {sub.planPackage.ctaButtonText && (
                <Button
                  appearance="outline"
                  style={{ alignSelf: "flex-end" }}
                  onPress={() => {
                    dispatch(setSelectedSubscription(sub));
                    toScehdule();
                  }}
                  size="small"
                >
                  {sub.planPackage.ctaButtonText}
                </Button>
              )}
            </Spacer>

            {showDetailsToggle[index] && (
              <View>
                <VoucherSubDetails
                  currentSub={sub}
                  accountVoucherSubs={accountVoucherSubs}
                />
                {childSubs.map((sub) => (
                  <View key={sub.id}>
                    <VoucherSubDetails
                      currentSub={sub}
                      accountVoucherSubs={accountVoucherSubs}
                    />
                  </View>
                ))}
              </View>
            )}
          </View>
        );
      })}
    </>
  );
};

export default VoucherDetails;
